import React, { useState, useEffect } from 'react';
import { Modal, List, Typography, Tag, Button, Input, Space, Tooltip, Empty } from 'antd';
import { HistoryOutlined, SearchOutlined, ClockCircleOutlined, UserOutlined, PushpinOutlined, PushpinFilled } from '@ant-design/icons';
import { useChat } from '../../contexts/ChatContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { useUser } from '../../contexts/UserContext';
import { sampleSidebarService } from '../../services/mock/sampleSidebarService';
import { useModelSelector } from '../../contexts/ModelSelectorContext';

const { Text, Title } = Typography;
const { Search } = Input;

interface ThreadHistoryProps {
  trigger?: React.ReactNode;
}

export const ThreadHistory: React.FC<ThreadHistoryProps> = ({ 
  trigger = (
    <Tooltip title="View Thread History">
      <Button 
        type="text" 
        icon={<HistoryOutlined />} 
        size="small"
        style={{ color: 'rgba(255, 255, 255, 0.65)' }}
      />
    </Tooltip>
  )
}) => {
  const { dataSource } = useModelSelector();
  const { threads, selectThread: chatSelectThread, currentThreadId, pinThread, unpinThread } = useChat();
  const { selectThread } = useNavigation();
  const { user, isAuthenticated } = useUser();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sampleThreads, setSampleThreads] = useState<any[]>([]);

  useEffect(() => {
    if (dataSource === 'sample' && isModalVisible) {
      void loadSampleThreads();
    }
  }, [dataSource, isModalVisible]);

  const loadSampleThreads = async () => {
    try {
      const threads = await sampleSidebarService.getSampleThreads();
      setSampleThreads(threads);
    } catch (error) {
      console.error('Failed to load sample threads:', error);
    }
  };

  const displayThreads = dataSource === 'sample' ? sampleThreads : threads;
  

  const handleThreadClick = (thread: any) => {
    // Use navigation context to select the thread (it will also handle chat context)
    void selectThread(thread.id);
    
    // Close the modal
    setIsModalVisible(false);
  };

  const handlePinToggle = async (thread: any, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent thread selection when clicking pin button
    
    try {
      console.log('📌 [ThreadHistory] Toggling pin for thread:', {
        threadId: thread.id,
        threadName: thread.name,
        currentIsPinned: thread.isPinned
      });
      if (thread.isPinned) {
        await unpinThread(thread.id);
      } else {
        await pinThread(thread.id);
      }
      console.log('✅ [ThreadHistory] Pin toggle successful');
    } catch (error) {
      console.error('❌ [ThreadHistory] Failed to toggle pin status:', error);
    }
  };

  const filteredThreads = displayThreads.filter(thread => {
    // First filter by user if authenticated and not in sample mode
    if (isAuthenticated && user && dataSource !== 'sample') {
      // Filter by thread participants or user ownership
      // For now, we'll show all threads since we don't have user-specific filtering yet
      // In a real implementation, you'd filter by thread.participants.includes(user.id)
    }
    
    // Then filter by search term
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      thread.name.toLowerCase().includes(searchLower) ||
      thread.description?.toLowerCase().includes(searchLower) ||
      thread.tags.some((tag: string) => tag.toLowerCase().includes(searchLower))
    );
  });

  const sortedThreads = filteredThreads.sort((a, b) => 
    new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 168) { // 7 days
      return `${Math.floor(diffInHours / 24)}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getThreadIcon = (thread: any) => {
    if (thread.isPinned) {
      return <Tag color="blue">Pinned</Tag>;
    }
    return <ClockCircleOutlined style={{ color: 'rgba(255, 255, 255, 0.45)' }} />;
  };

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <>
      <div onClick={() => setIsModalVisible(true)} style={{ cursor: 'pointer' }}>
        {trigger}
      </div>
      
      <Modal
        title={
          <Space>
            <HistoryOutlined />
            <span>Thread History</span>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              ({sortedThreads.length} of {displayThreads.length} threads)
            </Text>
          </Space>
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={600}
        style={{ top: 20 }}
        styles={{
          body: { maxHeight: '70vh', overflow: 'auto' }
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Search
            placeholder="Search threads by name, description, or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            prefix={<SearchOutlined />}
            allowClear
          />
          
          {sortedThreads.length === 0 ? (
            <Empty 
              description="No threads found"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <List
              dataSource={sortedThreads}
              renderItem={(thread) => (
                <List.Item
                  key={thread.id}
                  onClick={() => handleThreadClick(thread)}
                  style={{
                    cursor: 'pointer',
                    padding: '12px 16px',
                    borderRadius: '6px',
                    backgroundColor: thread.id === currentThreadId ? 'rgba(24, 144, 255, 0.1)' : 'transparent',
                    border: thread.id === currentThreadId ? '1px solid rgba(24, 144, 255, 0.3)' : '1px solid transparent',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (thread.id !== currentThreadId) {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (thread.id !== currentThreadId) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <List.Item.Meta
                    avatar={getThreadIcon(thread)}
                    title={
                      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Space>
                          <div
                            style={{ 
                              color: '#000000', 
                              fontSize: '14px', 
                              fontWeight: 'bold',
                              backgroundColor: '#e6f7ff',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid #1890ff',
                              display: 'inline-block',
                              minWidth: '100px'
                            }}
                          >
                            {thread.name || 'No Name'}
                          </div>
                          {thread.id === currentThreadId && (
                            <Tag color="blue">Current</Tag>
                          )}
                        </Space>
                        <Tooltip title={thread.isPinned ? "Unpin thread" : "Pin thread"}>
                          <Button
                            type="text"
                            size="small"
                            icon={thread.isPinned ? <PushpinFilled /> : <PushpinOutlined />}
                            onClick={(e) => handlePinToggle(thread, e)}
                            style={{ 
                              color: thread.isPinned ? '#faad14' : 'rgba(255, 255, 255, 0.45)',
                              padding: '2px 4px',
                              minWidth: 'auto'
                            }}
                          />
                        </Tooltip>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        {thread.description && (
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {thread.description}
                          </Text>
                        )}
                        
                        <Space size="small" wrap>
                          <Text type="secondary" style={{ fontSize: '11px' }}>
                            <ClockCircleOutlined /> {formatDate(thread.updatedAt)}
                          </Text>
                          <Text type="secondary" style={{ fontSize: '11px' }}>
                            <UserOutlined /> {thread.messageCount} messages
                          </Text>
                          <Text type="secondary" style={{ fontSize: '11px' }}>
                            Role: {thread.uiRole}
                          </Text>
                        </Space>
                        
                        {thread.tags && thread.tags.length > 0 && (
                          <Space size="small" wrap>
                            {thread.tags.slice(0, 3).map((tag: string, index: number) => (
                              <Tag key={index} color="default">
                                {tag}
                              </Tag>
                            ))}
                            {thread.tags.length > 3 && (
                              <Text type="secondary" style={{ fontSize: '11px' }}>
                                +{thread.tags.length - 3} more
                              </Text>
                            )}
                          </Space>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Space>
      </Modal>
    </>
  );
};
